import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import '@/styles/global.scss';
import './style.css';
import '@/styles/__custom-antd.scss';
import '@/styles/globalOverride.scss'; // for oveririding styles of antd components
import '@/styles/gtranslate-override.css'; // for hiding Google Translate elements
import '@/styles/language-selector.css'; // for styling the language selector
import { Provider } from 'react-redux';
import { store } from '@/store/index';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { ConfigProvider } from 'antd';

import { ResellerGlobalProvider } from './v1/context/resellerContext';

import { GlobalProvider } from './v1/context/GlobalContext';
import 'antd/dist/reset.css'; // For Ant Design 5.x
import { ToastContainer } from 'react-toastify'; // Import ToastContainer
import 'react-toastify/dist/ReactToastify.css'; // Import the default CSS for Toastify

import { PostHogProvider } from 'posthog-js/react';
import AppRouter from './routes';
import VersionChecker from './versionChecker';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import AudioPlayerToolbar from './components/AudioPlayerToolbar/AudioPlayerToolbar';
import ErrorBoundary from './ErrorBoundary';

// Add type declaration for gtranslateSettings

const options = {
	// api_host: import.meta.env.VITE_CLOUD_FRONT_DOMAIN_URL,
	api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
	// ui_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST,
	session_recording: {
		maskAllInputs: false,
		maskInputOptions: {
			password: false,
			color: false,
			date: false,
			'datetime-local': false,
			email: false,
			month: false,
			number: false,
			range: false,
			search: false,
			tel: false,
			text: false,
			time: false,
			url: false,
			week: false,
			textarea: false,
			select: false,
		},
	},
};

const queryClient = new QueryClient();

// DONOT define VITE_APP_SENTRY_DSN in development .env file
if (import.meta.env.VITE_APP_SENTRY_DSN) {
	Sentry.init({
		dsn: import.meta.env.VITE_APP_SENTRY_DSN,
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', /^https:\/\/app\.myaifrontdesk\.com/],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	});
}

const App = () => {
	const initializationRef = useRef(false);
	const googleTranslateElementRef = useRef<HTMLDivElement | null>(null);
	const googleTranslateScriptRef = useRef<HTMLScriptElement | null>(null);

	useEffect(() => {
		// Prevent multiple initializations
		if (initializationRef.current) {
			return;
		}

		// Check if Google Translate is already loaded
		if (window.google && window.google.translate) {
			initializationRef.current = true;
			return;
		}

		initializationRef.current = true;

		// Create Google Translate element
		const googleTranslateElement = document.createElement('div');
		googleTranslateElement.id = 'google_translate_element';
		googleTranslateElement.style.display = 'none';
		document.body.appendChild(googleTranslateElement);
		googleTranslateElementRef.current = googleTranslateElement;

		// Load Google Translate script
		const googleTranslateScript = document.createElement('script');
		googleTranslateScript.src =
			'//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
		googleTranslateScript.async = true;
		document.body.appendChild(googleTranslateScript);
		googleTranslateScriptRef.current = googleTranslateScript;

		// Initialize Google Translate
		window.googleTranslateElementInit = function () {
			// Use type assertion to tell TypeScript that google.translate exists
			const googleTranslate = window.google?.translate as any;
			if (googleTranslate) {
				try {
					new googleTranslate.TranslateElement(
						{
							pageLanguage: 'en',
							includedLanguages:
								'af,sq,am,ar,hy,az,eu,be,bn,bs,bg,ca,ceb,ny,zh-CN,zh-TW,co,hr,cs,da,nl,en,eo,et,tl,fi,fr,fy,gl,ka,de,el,gu,ht,ha,haw,iw,hi,hmn,hu,is,ig,id,ga,it,ja,jw,kn,kk,km,rw,ko,ku,ky,lo,la,lv,lt,lb,mk,mg,ms,ml,mt,mi,mr,mn,my,ne,no,or,ps,fa,pl,pt,pa,ro,ru,sm,gd,sr,st,sn,sd,si,sk,sl,so,es,su,sw,sv,tg,ta,tt,te,th,tr,tk,uk,ur,ug,uz,vi,cy,xh,yi,yo,zu',
							autoDisplay: false,
							layout: googleTranslate.TranslateElement.InlineLayout.SIMPLE,
							gaTrack: false, // Disable Google Analytics tracking
							gaId: '', // Empty Google Analytics ID
						},
						'google_translate_element',
					);

					// Add CSS to hide any Google Translate elements that might appear
					const style = document.createElement('style');
					style.textContent = `
						.goog-te-banner-frame, .skiptranslate, .goog-te-gadget, 
						.goog-te-menu-frame, .VIpgJd-ZVi9od-ORHb, .VIpgJd-ZVi9od-ORHb-OEVmcd, 
						.VIpgJd-ZVi9od-l4eHX-hSRGPd, .VIpgJd-ZVi9od-aZ2wEe {
							display: none !important;
							visibility: hidden !important;
							opacity: 0 !important;
						}
						body { top: 0 !important; position: static !important; }
					`;
					document.head.appendChild(style);

					// Simplified doGTranslate function that avoids recursive DOM manipulation
					window.doGTranslate = function (lang_pair: string) {
						const lang = lang_pair.split('|')[1];

						try {
							// Try to find existing Google Translate combo
							const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
							if (selectElement) {
								selectElement.value = lang;
								const event = new Event('change', { bubbles: true });
								selectElement.dispatchEvent(event);
								return;
							}

							// If no combo found, try iframe method
							const iframe = document.querySelector(
								'iframe.goog-te-menu-frame',
							) as HTMLIFrameElement;
							if (iframe) {
								try {
									const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
									if (iframeDoc) {
										const link = iframeDoc.querySelector(
											`a.goog-te-menu2-item[data-language="${lang}"]`,
										) as HTMLAnchorElement;
										if (link) {
											link.click();
											return;
										}
									}
								} catch (err) {
									console.error('Error in doGTranslate:', err);
								}
							}

							console.error(
								'Translation could not be applied directly - no Google Translate elements found',
							);
						} catch (error) {
							console.error('Error in doGTranslate:', error);
						}
					};
				} catch (error) {
					console.error('Error initializing Google Translate:', error);
				}
			}
		};

		// Cleanup function
		return () => {
			if (
				googleTranslateScriptRef.current &&
				document.body.contains(googleTranslateScriptRef.current)
			) {
				document.body.removeChild(googleTranslateScriptRef.current);
			}

			if (
				googleTranslateElementRef.current &&
				document.body.contains(googleTranslateElementRef.current)
			) {
				document.body.removeChild(googleTranslateElementRef.current);
			}

			// Clean up global functions
			if (window.googleTranslateElementInit) {
				window.googleTranslateElementInit = undefined;
			}
			if (window.doGTranslate) {
				window.doGTranslate = undefined;
			}

			// Reset initialization flag
			initializationRef.current = false;
		};
	}, []);

	return (
		<Provider store={store}>
			<QueryClientProvider client={queryClient}>
				<ConfigProvider
					theme={{
						token: {
							fontFamily: 'DM Sans, ui-sans-serif, system-ui, sans-serif',
						},
					}}
				>
					<GlobalProvider>
						<ResellerGlobalProvider>
							<AudioPlayerProvider>
								<PostHogProvider
									apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
									options={options}
								>
									<VersionChecker />
									<AppRouter />
									<ToastContainer />
									{/* Hidden element for Google Translate */}
									<div id="google_translate_trigger" style={{ display: 'none' }} />
								</PostHogProvider>
								<AudioPlayerToolbar />
							</AudioPlayerProvider>
						</ResellerGlobalProvider>
					</GlobalProvider>
				</ConfigProvider>
			</QueryClientProvider>
		</Provider>
	);
};

ReactDOM.createRoot(document.getElementById('root')!).render(
	<React.StrictMode>
		<ErrorBoundary>
			<App />
		</ErrorBoundary>
	</React.StrictMode>,
);
